import { FGCustomPanel, FieldGroup, IDataTableColumn, Loading } from "nsitools-react";
import * as React from "react";

import {
  BreedApi,
  BreedGridDto,
  BreedGroupApi,
  BreedGroupGridDto,
  BreedGroupSearch,
  BreedSearch,
  CompetitionRequestApi,
} from "../../../../api";
import { useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { useParams } from "react-router";
import { SmallFormGenerator, SRSHCustomSelector } from "../../../../components";

export interface ICompetitionBreedPanelProps {
  competitionRequestId: number;
  editMode: boolean;
}

export const CompetitionBreedPanel: React.FunctionComponent<ICompetitionBreedPanelProps> = ({
  competitionRequestId,
  editMode,
}) => {
  const breedApi = useApiService(BreedApi);
  const competitionRequestApi = useApiService(CompetitionRequestApi);
  const { id, state } = useParams<{ id: string; state: string }>();
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();

  const { data, loading, saveItem, saving } = useCrudApi(
    {
      getApiFn: async () => {
        const ids = await competitionRequestApi.getLinkedBreedIds(+id);
        return { breedIds: ids };
      },
      saveApiFn: (d) => competitionRequestApi.saveCompetitionBreeds(+id, d.breedIds),
      onSavedRoute: (d) => `${ERoutes.competitionRequest}/${+id}/breeds/edit`,
    },
    [id]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => t(ETLCodes.Breed),
        autoFitContent: true,
        fieldName: "name",
        render: (row: BreedGridDto) => (
          <div>
            {row.breedGroupLabel} - {row.name}
          </div>
        ),
      },
    ],
    [t]
  );

  const searchBreedIdsFunc = React.useCallback(
    (sObj) =>
      breedApi.getIds(
        BreedGroupSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
        })
      ),
    [breedApi, currentLanguage]
  );

  const searchBreedFunc = React.useCallback(
    (sObj) =>
      breedApi.baseSearch(
        BreedSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
        })
      ),
    [breedApi, currentLanguage]
  );

  return (
    <Loading isLoading={loading}>
      <SmallFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        editMode={state === "edit"}
        loading={loading}
        saving={saving}
      >
        <FieldGroup columns={1} fieldsetProps={{ title: t(ETLCodes.Breeds) }}>
          <FGCustomPanel>
            {(ctx) => (
              <SRSHCustomSelector
                linkedIds={ctx.formik.values?.breedIds}
                columns={columns}
                onLinkedIdsChanged={(ids) => ctx.formik.setFieldValue("breedIds", ids)}
                idField="breedId"
                searchFunction={searchBreedFunc}
                searchIdsFunc={searchBreedIdsFunc}
                editMode={state === "edit"}
              />
            )}
          </FGCustomPanel>
        </FieldGroup>
      </SmallFormGenerator>
    </Loading>
  );
};
